import type { WipSortFieldE } from 'declarations/enums/wip';
import type { WipRecordI } from 'declarations/interfaces/wip';
import type { WipColumnConfigI } from 'reduxStore/features/wip/wipSlice';

export interface WipItemCellI {
  id: keyof WipRecordI;
  label: string;
  alignment: 'left' | 'right' | 'center';
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  defaultVisible: boolean;
  isMoneyAmount?: boolean;
  sortable?: boolean;
  sortParameter?: WipSortFieldE;
  /**
   * Marks a column as required for the WIP entry row. Per the Chirp WIP spec (WARR-10507),
   * only `repairOrderNumber` is statically required; `submittedDate` is conditionally required
   * at runtime (see `validateWipRow`) when Claim Status is set to "Submitted".
   *
   * Required columns are also force-visible in the column picker so the field can never be
   * hidden out of the entry workflow.
   */
  required?: boolean;
}

export const WIP_ITEM_COLUMN_CONFIGURATION: WipItemCellI[] = [
  {
    id: 'repairOrderNumber',
    label: 'RO Number',
    alignment: 'left',
    minWidth: 100,
    defaultVisible: true,
    sortable: true,
    required: true,
  },
  {
    id: 'roofTopName',
    label: 'Dealership',
    alignment: 'left',
    minWidth: 180,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'oemFamilyName',
    label: 'OEM',
    alignment: 'left',
    minWidth: 70,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'openRepairOrderDate',
    label: 'Open RO Date',
    alignment: 'left',
    minWidth: 154,
    defaultVisible: true,
  },
  {
    id: 'age',
    label: 'Age',
    alignment: 'right',
    minWidth: 56,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'lineNumber',
    label: 'Line',
    alignment: 'left',
    minWidth: 60,
    defaultVisible: true,
  },
  {
    id: 'advisorNumber',
    label: 'Advisor Number',
    alignment: 'left',
    minWidth: 110,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'techNumber',
    label: 'Tech Number',
    alignment: 'left',
    minWidth: 100,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'oemClaimStatus',
    label: 'Claim Status',
    alignment: 'left',
    minWidth: 140,
    defaultVisible: true,
  },
  {
    id: 'assigneeName',
    label: 'Assigned To',
    alignment: 'left',
    minWidth: 160,
    defaultVisible: false,
  },
  {
    id: 'wipStatus',
    label: 'WIP Status',
    alignment: 'left',
    minWidth: 110,
    defaultVisible: true,
  },
  {
    id: 'priority',
    label: 'Priority',
    alignment: 'left',
    minWidth: 110,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'backlog',
    label: 'Backlog',
    alignment: 'left',
    minWidth: 80,
    defaultVisible: true,
  },
  {
    id: 'submittedDate',
    label: 'Submitted Date',
    alignment: 'left',
    minWidth: 154,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'isSublet',
    label: 'Sublet Flag',
    alignment: 'left',
    minWidth: 90,
    defaultVisible: false,
  },
  {
    id: 'repairOrderTotal',
    label: 'RO Total',
    alignment: 'right',
    minWidth: 100,
    isMoneyAmount: true,
    defaultVisible: false,
  },
  {
    id: 'partsAmountClaimed',
    label: 'Parts Amount Claimed',
    alignment: 'right',
    minWidth: 130,
    isMoneyAmount: true,
    defaultVisible: false,
  },
  {
    id: 'partsAmountPaid',
    label: 'Parts Amount Paid',
    alignment: 'right',
    minWidth: 130,
    isMoneyAmount: true,
    defaultVisible: false,
  },
  {
    id: 'laborAmountClaimed',
    label: 'Labor Amount Claimed',
    alignment: 'right',
    minWidth: 130,
    isMoneyAmount: true,
    defaultVisible: false,
  },
  {
    id: 'laborAmountPaid',
    label: 'Labor Amount Paid',
    alignment: 'right',
    minWidth: 130,
    isMoneyAmount: true,
    defaultVisible: false,
  },
  {
    id: 'techTime',
    label: 'Tech Time',
    alignment: 'right',
    minWidth: 90,
    defaultVisible: false,
  },
  {
    id: 'oemPaidTotal',
    label: 'OEM Paid Total',
    alignment: 'right',
    minWidth: 110,
    isMoneyAmount: true,
    defaultVisible: false,
  },
  {
    id: 'oemPaidDate',
    label: 'OEM Paid Date',
    alignment: 'left',
    minWidth: 130,
    defaultVisible: false,
    sortable: true,
  },
  {
    id: 'repairOrderStatusEnum',
    label: 'RO Status',
    alignment: 'left',
    minWidth: 110,
    defaultVisible: false,
  },
  {
    id: 'notes',
    label: 'Notes',
    alignment: 'left',
    minWidth: 200,
    defaultVisible: false,
  },
  {
    id: 'claimTotal',
    label: 'Claim Total',
    alignment: 'right',
    minWidth: 100,
    isMoneyAmount: true,
    defaultVisible: false,
  },
];

export const getDefaultVisibleWipColumns = (): WipItemCellI[] =>
  WIP_ITEM_COLUMN_CONFIGURATION.filter((c): boolean => c.defaultVisible);

/**
 * Build the picker's default column order using the table configuration. Required columns are
 * forced visible; everything else uses its `defaultVisible` flag. Mirrors WarrQueue's
 * `generateInitialOrder` in `useWIPColumnPicker`.
 */
export const generateDefaultWipColumnOrder = (): WipColumnConfigI[] =>
  WIP_ITEM_COLUMN_CONFIGURATION.map(
    (col): WipColumnConfigI => ({
      id: col.id,
      visible: col.required === true ? true : col.defaultVisible,
      required: col.required === true,
    })
  );

/**
 * Merge a stored column order with the current configuration:
 *  - removes columns no longer present in the configuration,
 *  - appends columns that were added since the user last saved (using config defaults),
 *  - re-stamps `required` from the configuration and forces required columns visible.
 * Mirrors WarrQueue's `appendMissingColumns` + `enrichWithRequired` helpers.
 */
export const reconcileWipColumnOrder = (stored: WipColumnConfigI[]): WipColumnConfigI[] => {
  const configById = new Map<string, WipItemCellI>(
    WIP_ITEM_COLUMN_CONFIGURATION.map((c): [string, WipItemCellI] => [c.id, c])
  );

  const enriched: WipColumnConfigI[] = stored
    .filter((c): boolean => configById.has(c.id))
    .map((c): WipColumnConfigI => {
      const cfg = configById.get(c.id);
      const required = cfg?.required === true;
      return { id: c.id, required, visible: required ? true : c.visible };
    });

  const presentIds = new Set<string>(enriched.map((c): string => c.id));
  for (const cfg of WIP_ITEM_COLUMN_CONFIGURATION) {
    if (presentIds.has(cfg.id)) continue;
    enriched.push({
      id: cfg.id,
      visible: cfg.required === true ? true : cfg.defaultVisible,
      required: cfg.required === true,
    });
  }
  return enriched;
};

/**
 * Resolve a stored column order into the `WipItemCellI[]` the WIP table actually renders.
 * Falls back to `getDefaultVisibleWipColumns()` when nothing has been persisted yet.
 */
export const resolveVisibleWipColumns = (
  columnOrder: readonly WipColumnConfigI[]
): WipItemCellI[] => {
  if (columnOrder.length === 0) return getDefaultVisibleWipColumns();
  const configById = new Map<string, WipItemCellI>(
    WIP_ITEM_COLUMN_CONFIGURATION.map((c): [string, WipItemCellI] => [c.id, c])
  );
  const result: WipItemCellI[] = [];
  for (const col of columnOrder) {
    if (!col.visible) continue;
    const cfg = configById.get(col.id);
    if (cfg) result.push(cfg);
  }
  return result;
};
